import React, { useState, useId, memo, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { GitBranch, ThumbsUp, ThumbsDown, MessageSquare } from 'lucide-react';
import FeedbackModal from './FeedbackModal';
import { useCompanyHover } from '../hooks/useCompanyHover';
import CompanyHoverCard from './CompanyHoverCard';
import '../styles/NewsCard.css';

const NewsCard = ({
  id,
  title = '',
  url = '',
  summary = '',
  newsTypes = [],
  alertLabel = '',
  publishedDate = '',
  companies = [],
  locations = [],
  sriRankValue = 0,
  isSelected = false,
  onSelect,
  userVote = null,
  onVote,
  isVoting = false,
  onOpenTimeline,
  onFilterClick,
  storylines = [],
}) => {
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const uniqueId = useId();
  const companiesListRef = useRef(null);

  // Check if this card's ID matches any storyline event_id
  const hasTimelineMatch = useMemo(() => {
    if (!id || !storylines?.length) return false;
    return storylines.some(sl =>
      sl?.timelines?.some(t => {
        const eventId = t?.event_id?.$oid || t?.event_id;
        return String(eventId) === String(id);
      })
    );
  }, [id, storylines]);

  // Company hover functionality
  const {
    hoveredCompany,
    companyData,
    isLoading: isLoadingCompanyInfo,
    error: companyInfoError,
    hoverPosition,
    handleMouseEnter,
    handleMouseLeave,
    handleCardMouseEnter,
    handleCardMouseLeave
  } = useCompanyHover();

  const handleVote = (type) => {
    if (isVoting || !onVote) return;
    const voteValue = type === 'like' ? 1 : -1;
    if ((type === 'like' && userVote === 1) || (type === 'dislike' && userVote === -1)) {
      onVote(id, 0);
    } else {
      onVote(id, voteValue);
    }
  };

  const handleTimelineClick = () => {
    if (onOpenTimeline) {
      onOpenTimeline(id, title, url);
    }
  };

  const companiesDisplay = companies.length > 0
    ? companies.map(c => c.name).filter(Boolean).join(' · ')
    : null;
  const starCount = Math.max(0, Math.min(5, Math.round(sriRankValue)));
  const articleForModal = { id, title, url, summary };

  // Calculate position based on the Companies List container
  let popoverStyle = {};
  if (hoveredCompany && companiesListRef.current) {
    const rect = companiesListRef.current.getBoundingClientRect();
    popoverStyle = {
      position: 'absolute',
      // Align with the left edge of the companies list
      left: `${rect.left + window.scrollX}px`,
      // Position directly below the companies list
      top: `${rect.bottom + window.scrollY}px`,
      zIndex: 10000
    };
  }

  return (
    <>
      <div className="news-card-news">
        <div className="news-card-header-news">
          <div className="news-card-checkbox-container-news">
            <input
              type="checkbox"
              id={`checkbox-${id}${uniqueId}`}
              className="news-card-checkbox-news"
              checked={isSelected}
              onChange={() => onSelect && onSelect(id)}
              aria-label={`Select ${title}`}
            />
            <label htmlFor={`checkbox-${id}${uniqueId}`} className="checkbox-label-news" />
          </div>
          <h3 className="news-card-title-news">
            <a href={url || '#'} target="_blank" rel="noopener noreferrer">
              {title || 'Untitled Article'}
            </a>
          </h3>
          {hasTimelineMatch && (
            <button className="timeline-button-news" title="View Timeline" onClick={handleTimelineClick}>
              <GitBranch size={18} />
            </button>
          )}
        </div>

        <p className="news-card-description-news">{summary}</p>
        <div className="tags-container-news">
          {newsTypes.map((newsType, idx) => (
            <span
              key={`type-${idx}`}
              className="tag-news tag-news-type-news"
              onClick={() => onFilterClick && onFilterClick('newsType', newsType.value || newsType.name || newsType)}
              style={{ cursor: onFilterClick ? 'pointer' : 'default' }}
              title={onFilterClick ? `Filter by ${newsType.name || newsType}` : ''}
            >
              {newsType.name || newsType}
            </span>
          ))}
          {alertLabel && (
            <span className="tag-news tag-alert-news">{alertLabel}</span>
          )}
          {locations.map((location, idx) => (
            <span
              key={`loc-${idx}`}
              className="tag-news tag-region-news"
              onClick={() => onFilterClick && onFilterClick('region', location.value || location.name || location)}
              style={{ cursor: onFilterClick ? 'pointer' : 'default' }}
              title={onFilterClick ? `Filter by ${location.name || location}` : ''}
            >
              🌍 {location.name || location}
            </span>
          ))}
        </div>

        {companies.length > 0 && (
          <div className="companies-list-news" ref={companiesListRef}>
            <span className="companies-label-news">Companies:</span>
            <span className="companies-names-news">
              {companies.map((company, idx) => (
                <span key={idx}>
                  <span
                    className="clickable-company-news"
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (onFilterClick) {
                        // Get company code
                        const companyCode = company.code || company.name || company;
                        const companyName = company.name || company;

                        // Try to get full name from hover data if it was already fetched
                        const fullCompanyName = (hoveredCompany === companyCode && companyData?.longName)
                          ? companyData.longName
                          : companyName;

                        onFilterClick('company', {
                          code: companyCode,
                          name: fullCompanyName
                        });
                      }
                    }}
                    onMouseEnter={(e) => {
                      handleMouseEnter(
                        company.code || company.name || company,
                        company.name || company,
                        e
                      );
                    }}
                    onMouseLeave={handleMouseLeave}
                    style={{ cursor: onFilterClick ? 'pointer' : 'default', position: 'relative' }}
                    title={onFilterClick ? `Filter by ${company.name || company}` : ''}
                  >
                    {company.name || company}
                  </span>
                  {idx < companies.length - 1 && ' · '}
                </span>
              ))}
            </span>
          </div>
        )}

        <div className="news-card-footer-news">
          <div className="actions-container-news">
            <button
              onClick={() => handleVote('like')}
              className={`action-button-news ${userVote === 1 ? 'active-like-news' : ''} ${isVoting ? 'voting-news' : ''}`}
              disabled={isVoting}
            >
              <ThumbsUp size={16} />
            </button>
            <button
              onClick={() => handleVote('dislike')}
              className={`action-button-news ${userVote === -1 ? 'active-dislike-news' : ''} ${isVoting ? 'voting-news' : ''}`}
              disabled={isVoting}
            >
              <ThumbsDown size={16} />
            </button>
            <button className="action-button-news" onClick={() => setIsFeedbackModalOpen(true)}>
              <MessageSquare size={16} />
            </button>
          </div>

          <div className="meta-container-news">
            {publishedDate && (
              <span className="date-text-news">{publishedDate}</span>
            )}
            <div className="stars-news">
              {[...Array(5)].map((_, i) => (
                <span key={i} className={i < starCount ? 'star-filled-news' : 'star-empty-news'}>★</span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Feedback Modal Portal */}
      {isFeedbackModalOpen && ReactDOM.createPortal(
        <FeedbackModal
          isOpen={isFeedbackModalOpen}
          onClose={() => setIsFeedbackModalOpen(false)}
          article={articleForModal}
        />,
        document.body
      )}

      {/* Company Hover Card Portal */}
      {hoveredCompany && companiesListRef.current && ReactDOM.createPortal(
        <div
          style={popoverStyle}
          onMouseEnter={handleCardMouseEnter}
          onMouseLeave={handleCardMouseLeave}
        >
          <CompanyHoverCard
            companyData={companyData}
            isLoading={isLoadingCompanyInfo}
            error={companyInfoError}
          />
        </div>,
        document.body
      )}
    </>
  );
};

export default memo(NewsCard);